import React from 'react';
import { motion } from 'framer-motion';
import { HiChatAlt, HiClock, HiExclamation } from 'react-icons/hi';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { PROBLEMS } from '../utils/constants';

const iconComponents = {
  MessageCircle: HiChatAlt,
  Clock: HiClock,
  AlertTriangle: HiExclamation,
};

const ProblemSection = () => {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section
      id="problem-section"
      ref={sectionRef}
      style={{
        backgroundColor: '#FAF7F2',
        padding: 'clamp(4rem, 8vw, 7rem) 1.5rem',
        borderTop: '1px solid var(--color-border)',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Two-column layout: heading left, accordion right */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '3rem',
        }} className="problem-grid">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: '#1A1A1A',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              The problem every Indian business faces
            </h2>
            <p style={{
              marginTop: '1rem',
              fontSize: '1rem',
              lineHeight: 1.65,
              color: '#666666',
              maxWidth: '400px',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              Manual processes don't scale. Your competitors reply in 30 seconds while you're still copy-pasting.
            </p>
          </motion.div>

          {/* Right: problem items as accordion-style list */}
          <div style={{ borderTop: '1px solid var(--color-border)' }}>
            {PROBLEMS.map((problem, index) => {
              const Icon = iconComponents[problem.icon];
              return (
                <motion.div
                  key={problem.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1, ease: 'easeOut' }}
                  style={{
                    borderBottom: '1px solid var(--color-border)',
                    padding: '1.5rem 0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{
                      flexShrink: 0,
                      width: '2rem',
                      height: '2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Icon size={20} color="#1A1A1A" />
                    </div>
                    <div>
                      <h3 style={{
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        color: '#1A1A1A',
                        marginBottom: '0.375rem',
                        fontFamily: '"DM Sans", sans-serif',
                      }}>
                        {problem.title}
                      </h3>
                      <p style={{
                        fontSize: '0.9rem',
                        lineHeight: 1.65,
                        color: '#666666',
                        fontFamily: '"DM Sans", sans-serif',
                      }}>
                        {problem.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          .problem-grid {
            grid-template-columns: 1fr 1.5fr !important;
            gap: 5rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default ProblemSection;
